import { Component, Fragment } from 'react'
import _ from 'lodash'
import { Form, SubmissionError } from 'redux-form'
import { Modal } from '@vzmi/shp_shared_compo'
import {
  formDecorator,
  Field,
  FormValueSelector
} from '../../../../../Common/forms'
import {
  TextareaField
} from '../../../../../Common/Form'
import {
  removeNotNumberAndComma
} from '../../../../../../lib/parser'
import { COMMA, idsOnlyParser } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import styles from './index.css'
import updateListingModelStyles from '../../index.css'
import { getAttrMap } from '../../helpers'
import { PRODUCT_KEY } from '../../constants'

export const FIELD = {
  PRODUCT_IDS: 'productIds'
}

export const CONFIG = {
  form: 'UPDATE_LISTING_MODEL_BATCH_REMOVE_FORM',
  initialValues: {
    [FIELD.PRODUCT_IDS]: ''
  }
}

export const getIsTwoLayerAttrFromRows = tableRows => {
  const isTwoLayerAttr = !_.isUndefined(
    _.get(tableRows, `0.${PRODUCT_KEY.ITEM_ID}`)
  )
  return isTwoLayerAttr
}

export const getExistTableRows = (tableRows, removedIdList, isTwoLayerAttr) => {
  const attrMap = getAttrMap(isTwoLayerAttr)
  const removedIdMap = _.keyBy(removedIdList)
  const existTableRows = _.filter(tableRows, row => {
    const currentId = _.get(row, attrMap.productId)
    const visible = _.get(row, PRODUCT_KEY.SPEC_VISIBLE)
    return (
      !(currentId in removedIdMap) &&
      visible
    )
  })
  return existTableRows
}

export const getInvalidSpecList = tableRows => {
  const groupedTableRows = _.groupBy(tableRows, PRODUCT_KEY.SPEC_SELECTED_VALUE)
  const invalidSpecList = _.transform(groupedTableRows, (specs, rows, spec) => {
    const validRows = _.filter(rows, row => (
      row[PRODUCT_KEY.SPEC_VISIBLE] &&
      row[PRODUCT_KEY.ITEM_SPEC_VISIBLE]
    ))
    if (_.size(validRows) !== 0) {
      return specs
    }
    specs.push(spec)
    return specs
  }, [])
  return invalidSpecList
}

export const getProductIdErrorMsg = (productIds, currentProductIdMap, tableRows) => {
  const isTwoLayerAttr = getIsTwoLayerAttrFromRows(tableRows)
  const productIdList = idsOnlyParser(productIds)
  const notFoundIdList = _.filter(productIdList, id => !(id in currentProductIdMap))
  if (!_.isEmpty(notFoundIdList)) {
    const notFoundIdString = _.join(notFoundIdList, `${COMMA} `)
    return `商品編號 ${notFoundIdString} 不在此賣場，請調整後再送出`
  }

  const duplicateIdList = _.flow(
    () => _.countBy(productIdList),
    countedMap => _.omitBy(countedMap, value => value === 1),
    _.keys
  )()
  if (!_.isEmpty(duplicateIdList)) {
    const duplicateIdString = _.join(duplicateIdList, `${COMMA} `)
    return `重複商品編號 ${duplicateIdString}`
  }

  const existTableRows = getExistTableRows(tableRows, productIdList, isTwoLayerAttr)

  if (_.isEmpty(existTableRows)) {
    return '賣場最少需留下一個可顯示的商品，請調整後再送出'
  }

  return null
}

export const getTwoLayerInvalidProductIdsMap = (
  removedIdList,
  tableRows,
  groupedTableRowsBySpec
) => {
  const isTwoLayerAttr = getIsTwoLayerAttrFromRows(tableRows)
  if (!isTwoLayerAttr) {
    return null
  }

  const attrMap = getAttrMap(isTwoLayerAttr)
  const existTableRows = getExistTableRows(tableRows, removedIdList, attrMap)
  const invalidSpecList = getInvalidSpecList(existTableRows)

  if (!_.isEmpty(invalidSpecList)) {
    const removedIdMap = _.keyBy(removedIdList)
    const invalidProductIdsMap = _.reduce(invalidSpecList, (map, spec) => {
      const currentSpecRows = _.get(groupedTableRowsBySpec, spec, [])
      const selectedRowIds = _.flow(
        () => _.filter(currentSpecRows, row => (
          _.get(row, attrMap.productId) in removedIdMap
        )),
        selectedRows => _.map(selectedRows, attrMap.productId)
      )()
      map[spec] = selectedRowIds
      return map
    }, {})
    return invalidProductIdsMap
  }
  return null
}

export const getSubmitError = (values, tableRows, groupedTableRowsBySpec) => {
  const productIds = _.get(values, FIELD.PRODUCT_IDS)
  const productIdList = idsOnlyParser(productIds)
  const twoLayerInvalidProductIdsMap = getTwoLayerInvalidProductIdsMap(
    productIdList,
    tableRows,
    groupedTableRowsBySpec
  )

  if (!_.isEmpty(twoLayerInvalidProductIdsMap)) {
    const invalidSpecs = _.keys(twoLayerInvalidProductIdsMap)
    return {
      [FIELD.PRODUCT_IDS]: (
        <>
          以下商品因為移除後只剩下隱藏商品所以無法移除
          <br />
          {_.map(invalidSpecs, spec => {
            const invalidProductIds = _.get(twoLayerInvalidProductIdsMap, spec, [])
            return (
              <Fragment key={spec}>
                {`[${spec}] ${_.join(invalidProductIds, `${COMMA} `)}`}
                <br />
              </Fragment>
            )
          })}
        </>
      )
    }
  }

  return null
}

class BatchRemoveModal extends Component {
  constructor () {
    super()
    this.state = {
      productIdMap: {},
      groupedTableRowsBySpec: {}
    }
  }

  componentDidUpdate (prevProps) {
    if (_.isEqual(prevProps.tableRows, this.props.tableRows)) {
      return null
    }
    this.updateTableRows()
  }

  updateTableRows = () => {
    const { tableRows, isTwoLayerAttr } = this.props
    const attrMap = getAttrMap(isTwoLayerAttr)
    const productIdMap = _.flow(
      () => _.map(tableRows, row => _.get(row, attrMap.productId)),
      _.keyBy
    )()
    const groupedTableRowsBySpec = _.groupBy(tableRows, row => _.get(row, PRODUCT_KEY.SPEC_SELECTED_VALUE))
    this.setState({ productIdMap, groupedTableRowsBySpec })
  }

  productIdValidation = (productIds, values, fieldProps) => {
    const { tableRows } = this.props
    const { productIdMap } = this.state
    const errorMsg = getProductIdErrorMsg(productIds, productIdMap, tableRows)
    return errorMsg
  }

  onSubmit = values => {
    const { onBatchRemove, tableRows } = this.props
    const { groupedTableRowsBySpec } = this.state
    const productIds = _.get(values, FIELD.PRODUCT_IDS, [])
    const submitError = getSubmitError(values, tableRows, groupedTableRowsBySpec)
    if (!_.isEmpty(submitError)) {
      throw new SubmissionError(submitError)
    }
    const productIdList = _.map(idsOnlyParser(productIds), _.toString)
    onBatchRemove(productIdList)
  }

  onCancel = () => {
    const { reset, onCancel } = this.props
    onCancel()
    reset()
  }

  render () {
    const {
      visible,
      onCancel,
      submitting,
      invalid,
      handleSubmit,
      ...restProps
    } = this.props
    return (
      <FormValueSelector
        form={CONFIG.form}
        fieldName={FIELD.PRODUCT_IDS}
      >
        {productIds => {
          const isOkButtonDisabled = (
            submitting ||
            invalid ||
            _.isEmpty(productIds)
          )
          return (
            <Modal
              title={<h1>批次移除項目商品</h1>}
              visible={visible}
              onOk={handleSubmit(this.onSubmit)}
              onCancel={this.onCancel}
              okText='移除商品'
              className='batch-remove-modal'
              okButtonDisabled={isOkButtonDisabled}
              {...restProps}
            >
              <Form onSubmit={this.onSubmit}>
                <p className={updateListingModelStyles.condition}>
                  請輸入商品編號
                </p>
                <Field
                  name={FIELD.PRODUCT_IDS}
                  data-role={FIELD.PRODUCT_IDS}
                  component={TextareaField}
                  className={styles.listingId}
                  placeholder='請以逗號或 ENTER 分隔'
                  parse={removeNotNumberAndComma}
                  validate={this.productIdValidation}
                  keepErrorPosition
                />
              </Form>
            </Modal>
          )
        }}
      </FormValueSelector>
    )
  }
}

export default formDecorator(CONFIG)(BatchRemoveModal)
